import React, { useMemo, useState } from 'react';
import {
  Box, Typography, IconButton, Tooltip, Collapse, Chip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapsedIcon,
  TrendingUp as UpIcon,
  TrendingDown as DownIcon,
} from '@mui/icons-material';
import {
  DreSummaryRow,
  DreSummaryRowKind,
  DreComputeRow,
  formatBRL,
  formatPct,
  MONTHS_PT_SHORT,
  bucketToMonthIndex,
} from '../../services/dreService';

// =============================================================================
// Tipos
// =============================================================================

export type DreColumnMode = 'realized' | 'expected' | 'both';

export interface DreTableProps {
  summaryRows: DreSummaryRow[];
  detailRows: DreComputeRow[];
  loading?: boolean;
  showAv?: boolean;
  showAh?: boolean;
  showYearTotal?: boolean;
  columnMode?: DreColumnMode;
  onCellClick?: (params: {
    rowKind?: DreSummaryRowKind;
    accountId?: string;
    accountIds?: string[];
    bucket: string;
  }) => void;
}

// =============================================================================
// Configuração visual das linhas sintéticas
// =============================================================================

interface RowStyleConfig {
  expandable?: boolean;     // tem detalhamento
  emphasis?: 'header' | 'subtotal' | 'total' | 'highlight' | 'footer';
  paddingLeft?: number;
  fontWeight?: number;
  textColor?: string;
  bgColor?: string;
}

const ROW_STYLES: Partial<Record<DreSummaryRowKind, RowStyleConfig>> = {
  REVENUE_TOTAL:        { emphasis: 'header', fontWeight: 800, bgColor: '#EFF6FF', textColor: '#1E3A8A' },
  REVENUE_DEDUCTION:    { expandable: true, paddingLeft: 24, textColor: '#9F1239' },
  NET_REVENUE:          { emphasis: 'subtotal', fontWeight: 700, textColor: '#1E40AF' },
  VARIABLE_COST:        { expandable: true, fontWeight: 600 },
  CONTRIBUTION_MARGIN:  { emphasis: 'subtotal', fontWeight: 700, bgColor: '#F0FDF4', textColor: '#14532D' },
  FIXED_EXPENSE:        { expandable: true, fontWeight: 600 },
  PROFIT_BEFORE_INV:    { emphasis: 'subtotal', fontWeight: 700, textColor: '#1F2937' },
  INVESTMENT:           { expandable: true, fontWeight: 600 },
  OPERATING_EXPENSE:    { fontWeight: 600 },
  OPERATING_PROFIT:     { emphasis: 'subtotal', fontWeight: 700, bgColor: '#EEF2FF', textColor: '#3730A3' },
  NON_OPERATIONAL:      { expandable: true, fontWeight: 600 },
  NET_RESULT:           { emphasis: 'highlight', fontWeight: 800, bgColor: '#FEF9C3', textColor: '#854D0E' },
  BREAKEVEN_NO_INV:     { emphasis: 'footer', textColor: '#475569' },
  BREAKEVEN_WITH_INV:   { emphasis: 'footer', textColor: '#475569' },
  CASH_RECONCILE:       { emphasis: 'footer', textColor: '#475569' },
};

// Mapa rowKind → dre_group(s) para determinar contas filhas exibidas no detalhe
const ROW_KIND_TO_GROUPS: Partial<Record<DreSummaryRowKind, string[]>> = {
  REVENUE_TOTAL:       ['REVENUE'],
  REVENUE_DEDUCTION:   ['REVENUE_DEDUCTION'],
  VARIABLE_COST:       ['VARIABLE_COST'],
  FIXED_EXPENSE:       ['FIXED_EXPENSE'],
  INVESTMENT:          ['INVESTMENT'],
  NON_OPERATIONAL:     ['NON_OP_INCOME', 'NON_OP_EXPENSE'],
};

/** Uma linha de detalhe por conta (valores agregados por mês). */
interface DetailAccountRow {
  account: DreComputeRow;
  byBucket: Record<string, {
    realized: number;
    expected: number;
    avPct: number | null;
    ahPct: number | null;
    valueForRegime: number;
  }>;
}

/** Linha de detalhe após merge por código — inclui todos os UUID para drill-down. */
interface MergedDetailAccountRow {
  account: DreComputeRow;
  mergedAccountIds: string[];
  byBucket: Record<string, {
    realized: number;
    expected: number;
    avPct: number | null;
    ahPct: number | null;
    valueForRegime: number;
  }>;
}

/**
 * PostgreSQL trata NULL como distinto em UNIQUE (unit_id, code): várias contas
 * globais (unit_id NULL) com o mesmo código podem existir — o DRE devolve uma
 * linha por UUID e a expansão repetia o mesmo código. Unimos por account_code.
 */
function mergeExpandedAccountsByCode(entries: DetailAccountRow[]): MergedDetailAccountRow[] {
  const map = new Map<string, MergedDetailAccountRow>();
  for (const entry of entries) {
    const code = entry.account.account_code;
    const existing = map.get(code);
    if (!existing) {
      map.set(code, {
        account: entry.account,
        mergedAccountIds: [entry.account.account_id],
        byBucket: { ...entry.byBucket },
      });
      continue;
    }
    if (!existing.mergedAccountIds.includes(entry.account.account_id)) {
      existing.mergedAccountIds.push(entry.account.account_id);
    }
    const merged = { ...existing.byBucket };
    for (const [bk, cell] of Object.entries(entry.byBucket)) {
      const prev = merged[bk];
      if (!prev) {
        merged[bk] = { ...cell };
      } else {
        merged[bk] = {
          realized: (Number(prev.realized) || 0) + (Number(cell.realized) || 0),
          expected: (Number(prev.expected) || 0) + (Number(cell.expected) || 0),
          valueForRegime: (Number(prev.valueForRegime) || 0) + (Number(cell.valueForRegime) || 0),
          avPct: null,
          ahPct: null,
        };
      }
    }
    map.set(code, { account: existing.account, mergedAccountIds: existing.mergedAccountIds, byBucket: merged });
  }
  return Array.from(map.values());
}

// =============================================================================
// Colunas internas dos meses (REALIZADO + AV + AH) — mesmo template no header e nas células
// =============================================================================

/** Coluna monetária com largura mínima para exibir valores BRL (+ / - grandes); AV/AH fixos compactos. */
function dreMonthInnerGridCols(showAv: boolean, showAh: boolean): string {
  const parts = ['minmax(10.25rem, 1fr)'];
  if (showAv) parts.push('72px');
  if (showAh) parts.push('72px');
  return parts.join(' ');
}

/** gap + padding consistentes dentro de cada “mês” */
const MONTH_CELL_INNER_GAP = 1.25;
const MONTH_CELL_INNER_PY = 0.75;

// =============================================================================
// Cells
// =============================================================================

interface CellProps {
  realized: number;
  expected?: number;
  avPct: number | null;
  ahPct: number | null;
  showAv: boolean;
  showAh: boolean;
  columnMode: DreColumnMode;
  emphasized?: boolean;
  onClick?: () => void;
  isCashReconcile?: boolean;
}

const Cell: React.FC<CellProps> = ({
  realized, expected, avPct, ahPct, showAv, showAh, columnMode, emphasized, onClick, isCashReconcile,
}) => {
  const value = columnMode === 'expected' ? (expected ?? realized) : realized;

  const gridCols = dreMonthInnerGridCols(showAv, showAh);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        columnGap: MONTH_CELL_INNER_GAP,
        alignItems: 'center',
        px: 1.25,
        py: MONTH_CELL_INNER_PY,
        cursor: onClick ? 'pointer' : 'default',
        borderRight: '1px solid rgba(226, 232, 240, 0.85)',
        minWidth: 0 /* filho monetário usa min-width no track do grid */,
        '&:hover': onClick ? { bgcolor: 'rgba(14, 165, 233, 0.08)' } : {},
        transition: 'background 0.15s',
      }}
      onClick={onClick}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
          fontSize: '0.78rem',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: emphasized ? 700 : 500,
          color: value < 0 ? '#dc2626' : value > 0 ? '#0f172a' : '#94a3b8',
          textAlign: 'right',
          whiteSpace: 'nowrap',
          minWidth: 'min-content',
          borderRight:
            showAv || showAh
              ? '1px solid rgba(226, 232, 240, 0.7)'
              : 'none',
          pr: showAv || showAh ? MONTH_CELL_INNER_GAP : 0,
        }}
      >
        {formatBRL(value)}
      </Typography>
      {showAv && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            minHeight: '1.35rem',
            pl: 0.25,
            borderRight: showAh ? '1px solid rgba(226, 232, 240, 0.55)' : 'none',
            pr: showAh ? MONTH_CELL_INNER_GAP : 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.68rem',
              fontWeight: 600,
              color: isCashReconcile ? '#94a3b8' : '#475569',
              textAlign: 'right',
              whiteSpace: 'nowrap',
              fontFamily: '"JetBrains Mono", monospace',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {avPct !== null && avPct !== undefined ? formatPct(avPct, 1) : '—'}
          </Typography>
        </Box>
      )}
      {showAh && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.35 }}>
          {ahPct !== null && ahPct !== undefined && (
            ahPct > 0 ? <UpIcon sx={{ fontSize: 11, color: '#16a34a', flexShrink: 0 }} /> :
            ahPct < 0 ? <DownIcon sx={{ fontSize: 11, color: '#dc2626', flexShrink: 0 }} /> : null
          )}
          <Typography
            sx={{
              fontSize: '0.68rem', fontWeight: 600,
              color: ahPct === null || ahPct === undefined ? '#cbd5e1' :
                     ahPct > 0 ? '#15803d' : ahPct < 0 ? '#dc2626' : '#94a3b8',
              fontFamily: '"JetBrains Mono", monospace',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
              minWidth: '2.85rem',
              textAlign: 'right',
            }}
          >
            {ahPct !== null && ahPct !== undefined ? formatPct(ahPct, 1) : '—'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// Cabeçalho
// =============================================================================

interface HeaderProps {
  buckets: string[];
  showAv: boolean;
  showAh: boolean;
  showYearTotal: boolean;
  /** Rótulo da subcoluna de valores (alinha ao regime caixa/competência) */
  valueColumnTitle?: string;
}

/** Soma razoável: padding + gaps + índices + coluna monetária mín (~10.25rem) — evita ellipsis no REALIZADO. */
function dreMonthOuterMinPx(subColCount: number): number {
  if (subColCount >= 3) return 360;
  if (subColCount === 2) return 280;
  return 152;
}

const TableHeader: React.FC<HeaderProps> = ({ buckets, showAv, showAh, showYearTotal, valueColumnTitle = 'REALIZADO' }) => {
  const subColCount = 1 + (showAv ? 1 : 0) + (showAh ? 1 : 0);
  const outerMinPx = dreMonthOuterMinPx(subColCount);
  const monthInnerCols = dreMonthInnerGridCols(showAv, showAh);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `minmax(280px, 320px) repeat(${buckets.length + (showYearTotal ? 1 : 0)}, minmax(${outerMinPx}px, 1fr))`,
        width: '100%',
        minWidth: 'max-content',
        position: 'sticky', top: 0, zIndex: 6,
        bgcolor: '#0C6FC1',
        color: '#fff',
      }}
    >
      <Box sx={{ p: 1.5, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1, borderRight: '1px solid rgba(255,255,255,0.15)' }}>
        MOSTRAR CONTAS FILHAS
      </Box>
      {buckets.map((b) => (
        <Box key={b} sx={{
          display: 'grid',
          gridTemplateRows: 'auto auto',
          borderRight: '1px solid rgba(255,255,255,0.15)',
        }}>
          <Box sx={{ textAlign: 'center', py: 1, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.5, borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
            {MONTHS_PT_SHORT[bucketToMonthIndex(b)]}
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: monthInnerCols,
            columnGap: MONTH_CELL_INNER_GAP,
            alignItems: 'center',
            px: 1.25,
            py: 0.5,
            borderTop: '1px solid rgba(255,255,255,0.12)',
            fontSize: '0.62rem',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.88)',
          }}>
            <Box sx={{
              textAlign: 'right',
              borderRight: showAv || showAh ? '1px solid rgba(255,255,255,0.2)' : 'none',
              pr: showAv || showAh ? MONTH_CELL_INNER_GAP : 0,
            }}>
              {valueColumnTitle}
            </Box>
            {showAv && (
              <Box sx={{
                textAlign: 'right',
                borderRight: showAh ? '1px solid rgba(255,255,255,0.2)' : 'none',
                pr: showAh ? MONTH_CELL_INNER_GAP : 0,
              }}
              >
                AV
              </Box>
            )}
            {showAh && <Box sx={{ textAlign: 'right', letterSpacing: 0.3 }}>AH</Box>}
          </Box>
        </Box>
      ))}
      {showYearTotal && (
        <Box sx={{
          display: 'grid', gridTemplateRows: 'auto auto', bgcolor: '#0a5da0',
          borderLeft: '2px solid rgba(255,255,255,0.3)',
        }}>
          <Box sx={{ textAlign: 'center', py: 1, fontSize: '0.72rem', fontWeight: 800, letterSpacing: 0.5, borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
            ANO
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: monthInnerCols,
            columnGap: MONTH_CELL_INNER_GAP,
            alignItems: 'center',
            px: 1.25,
            py: 0.5,
            borderTop: '1px solid rgba(255,255,255,0.15)',
            fontSize: '0.62rem',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.88)',
          }}>
            <Box sx={{
              textAlign: 'right',
              borderRight: showAv || showAh ? '1px solid rgba(255,255,255,0.2)' : 'none',
              pr: showAv || showAh ? MONTH_CELL_INNER_GAP : 0,
            }}>
              TOTAL
            </Box>
            {showAv && (
              <Box sx={{
                textAlign: 'right',
                borderRight: showAh ? '1px solid rgba(255,255,255,0.2)' : 'none',
                pr: showAh ? MONTH_CELL_INNER_GAP : 0,
              }}
              >
                AV
              </Box>
            )}
            {showAh && (
              <Box sx={{ textAlign: 'right', visibility: 'hidden', userSelect: 'none' }} aria-hidden>
                AH
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// Linha
// =============================================================================

interface RowProps {
  rowKind?: DreSummaryRowKind;
  label: string;
  buckets: string[];
  byBucket: Record<string, { realized: number; expected: number; avPct: number | null; ahPct: number | null; valueForRegime: number }>;
  style?: RowStyleConfig;
  showAv: boolean;
  showAh: boolean;
  showYearTotal: boolean;
  columnMode: DreColumnMode;
  onCellClick?: (bucket: string) => void;
  expanded?: boolean;
  onToggle?: () => void;
  level?: number;
  expandable?: boolean;
}

const SummaryRow: React.FC<RowProps> = ({
  rowKind, label, buckets, byBucket, style = {}, showAv, showAh, showYearTotal,
  columnMode, onCellClick, expanded, onToggle, level = 0, expandable,
}) => {
  const subColCount = 1 + (showAv ? 1 : 0) + (showAh ? 1 : 0);

  const outerMinPx = dreMonthOuterMinPx(subColCount);

  // Total ano
  const yearTotal = useMemo(() => {
    const all = buckets.map((b) => byBucket[b]).filter(Boolean);
    if (all.length === 0) return null;
    const realized = all.reduce((s, x) => s + (x.realized || 0), 0);
    const expected = all.reduce((s, x) => s + (x.expected || 0), 0);
    const valueForRegime = all.reduce((s, x) => s + (x.valueForRegime || 0), 0);
    return { realized, expected, valueForRegime, avPct: null, ahPct: null };
  }, [buckets, byBucket]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `minmax(280px, 320px) repeat(${buckets.length + (showYearTotal ? 1 : 0)}, minmax(${outerMinPx}px, 1fr))`,
        width: '100%',
        minWidth: 'max-content',
        bgcolor: style.bgColor || (level > 0 ? 'rgba(248, 250, 252, 0.5)' : '#fff'),
        borderBottom: '1px solid #e2e8f0',
        '&:hover': { bgcolor: style.bgColor ? style.bgColor : '#f8fafc' },
        transition: 'background 0.15s',
      }}
    >
      {/* Label sticky */}
      <Box sx={{
        position: 'sticky', left: 0, zIndex: 2,
        bgcolor: style.bgColor || (level > 0 ? '#f8fafc' : '#fff'),
        display: 'flex', alignItems: 'center', gap: 0.5,
        pl: (style.paddingLeft || 16) / 8 + level * 2,
        pr: 1.5, py: 0.85,
        fontSize: '0.78rem', fontWeight: style.fontWeight || 500,
        color: style.textColor || '#1e293b',
        borderRight: '1px solid #e2e8f0',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {expandable && (
          <IconButton size="small" onClick={onToggle} sx={{ p: 0, color: 'inherit' }}>
            {expanded ? <ExpandIcon fontSize="small" /> : <CollapsedIcon fontSize="small" />}
          </IconButton>
        )}
        <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textTransform: style.emphasis === 'header' ? 'uppercase' : 'none', letterSpacing: style.emphasis === 'header' ? 0.5 : 0 }}>
          {label}
        </Typography>
      </Box>

      {buckets.map((b) => {
        const cell = byBucket[b] || { realized: 0, expected: 0, avPct: null, ahPct: null, valueForRegime: 0 };
        return (
          <Cell
            key={b}
            realized={cell.realized}
            expected={cell.expected}
            avPct={cell.avPct}
            ahPct={cell.ahPct}
            showAv={showAv}
            showAh={showAh}
            columnMode={columnMode}
            emphasized={style.emphasis === 'subtotal' || style.emphasis === 'highlight'}
            onClick={onCellClick ? () => onCellClick(b) : undefined}
            isCashReconcile={rowKind === 'CASH_RECONCILE'}
          />
        );
      })}

      {showYearTotal && yearTotal && (
        <Cell
          realized={yearTotal.realized}
          expected={yearTotal.expected}
          avPct={null}
          ahPct={null}
          showAv={showAv}
          showAh={showAh}
          columnMode={columnMode}
          emphasized
        />
      )}
    </Box>
  );
};

// =============================================================================
// Componente principal
// =============================================================================

export const DreTable: React.FC<DreTableProps> = ({
  summaryRows,
  detailRows,
  loading = false,
  showAv = true,
  showAh = true,
  showYearTotal = true,
  columnMode = 'realized',
  onCellClick,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<DreSummaryRowKind>>(new Set());

  const valueColumnTitle = columnMode === 'expected' ? 'ESPERADO' : 'REALIZADO';

  const buckets = useMemo(() => {
    const set = new Set(summaryRows.map((r) => r.bucket));
    return Array.from(set).sort();
  }, [summaryRows]);

  // Mapa: rowKind → bucket → valores
  const rowMap = useMemo(() => {
    const map = new Map<DreSummaryRowKind, Record<string, any>>();
    for (const r of summaryRows) {
      if (!map.has(r.row_kind)) map.set(r.row_kind, {});
      map.get(r.row_kind)![r.bucket] = {
        realized: r.realized, expected: r.expected,
        avPct: r.av_pct, ahPct: r.ah_pct,
        valueForRegime: r.value_for_regime,
      };
    }
    return map;
  }, [summaryRows]);

  // Detalhe por dre_group → list of accounts
  const detailByGroup = useMemo(() => {
    const m = new Map<string, Map<string, { account: DreComputeRow; byBucket: Record<string, any> }>>();
    for (const d of detailRows) {
      if (!m.has(d.dre_group)) m.set(d.dre_group, new Map());
      const grp = m.get(d.dre_group)!;
      if (!grp.has(d.account_id)) grp.set(d.account_id, { account: d, byBucket: {} });
      grp.get(d.account_id)!.byBucket[d.bucket] = {
        realized: d.realized, expected: d.expected,
        avPct: d.av_pct, ahPct: d.ah_pct,
        valueForRegime: d.value_for_regime,
      };
    }
    return m;
  }, [detailRows]);

  const toggle = (kind: DreSummaryRowKind) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind); else next.add(kind);
      return next;
    });
  };

  const orderedKinds: DreSummaryRowKind[] = useMemo(() => {
    const seen = new Set<DreSummaryRowKind>();
    return summaryRows
      .sort((a, b) => a.row_order - b.row_order)
      .map((r) => r.row_kind)
      .filter((k) => { if (seen.has(k)) return false; seen.add(k); return true; });
  }, [summaryRows]);

  const handleCellClick = (rowKind: DreSummaryRowKind, bucket: string) => {
    if (onCellClick) onCellClick({ rowKind, bucket });
  };

  return (
    <Box sx={{
      bgcolor: '#fff', borderRadius: 2, overflow: 'auto',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 24px rgba(15, 23, 42, 0.06)',
      maxHeight: '100%',
      position: 'relative',
    }}>
      {loading && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Calculando DRE…</Typography>
        </Box>
      )}

      <Box
        sx={{
          width: 'max-content',
          minWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <TableHeader buckets={buckets} showAv={showAv} showAh={showAh} showYearTotal={showYearTotal} valueColumnTitle={valueColumnTitle} />

        {orderedKinds.map((kind) => {
          const r = summaryRows.find((s) => s.row_kind === kind);
          if (!r) return null;
          const style = ROW_STYLES[kind] || {};
          const byBucket = rowMap.get(kind) || {};
          const expandable = !!style.expandable && (ROW_KIND_TO_GROUPS[kind]?.length || 0) > 0;
          const expanded = expandedRows.has(kind);
          const groups = ROW_KIND_TO_GROUPS[kind] || [];

          return (
            <React.Fragment key={kind}>
              <SummaryRow
                rowKind={kind}
                label={r.row_label}
                buckets={buckets}
                byBucket={byBucket}
                style={style}
                showAv={showAv}
                showAh={showAh}
                showYearTotal={showYearTotal}
                columnMode={columnMode}
                expandable={expandable}
                expanded={expanded}
                onToggle={expandable ? () => toggle(kind) : undefined}
                onCellClick={(bucket) => handleCellClick(kind, bucket)}
              />

              <Collapse in={expandable && expanded} timeout={250}>
                {groups.flatMap((g) => {
                  const raw = Array.from(detailByGroup.get(g)?.values() || []).sort(
                    (a, b) =>
                      a.account.sort_order - b.account.sort_order ||
                      a.account.account_code.localeCompare(b.account.account_code),
                  );
                  const accs = mergeExpandedAccountsByCode(raw);
                  return accs.map((a) => (
                    <SummaryRow
                      key={`${g}-${a.account.account_code}`}
                      label={`${a.account.account_code} · ${a.account.account_name}`}
                      buckets={buckets}
                      byBucket={a.byBucket}
                      style={{ paddingLeft: 56 }}
                      showAv={showAv}
                      showAh={showAh}
                      showYearTotal={showYearTotal}
                      columnMode={columnMode}
                      level={1}
                      onCellClick={
                        onCellClick
                          ? (bucket) => onCellClick({ accountIds: a.mergedAccountIds, bucket })
                          : undefined
                      }
                    />
                  ));
                })}
              </Collapse>
            </React.Fragment>
          );
        })}
      </Box>

      {summaryRows.length === 0 && !loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Sem dados no período selecionado.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DreTable;
